import React, { useCallback, useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import './DISCDialogComponent.scss';
import { SlideTransition } from '../../../Transition/Transition';
import { makeStyles } from 'tss-react/mui';
import MessageSquare from 'lucide-react/dist/esm/icons/message-square';
import { slidingToggleButtonNoBorder, slidingToggleGroupNoBorder } from '../../../SlidingToggleButton/noBorderStyles';
import { discHint, discTag } from '../../../../constants/disc';

// Per-DISC accent used to colour the "Approach" callout so it reads "live" and
// matches the selected type. Same hue family as the letter rows below
// (D coral / I gold / S green / C blue), with a deeper readable accent and a
// soft tint of it for the background.
const DISC_CALLOUT_COLORS = {
  D: { accent: '#e05a3c', bg: '#fdeee9' },
  I: { accent: '#c99414', bg: '#fdf6e1' },
  S: { accent: '#22a34a', bg: '#e8f8ed' },
  C: { accent: '#2f7fd1', bg: '#e8f2fc' }
};

const useStyles = makeStyles()(theme => ({
  toggleGroup: {
    position: 'relative',
    backgroundColor: '#f3f5fb',
    borderRadius: '16px !important',
    padding: 2,
    zIndex: 0,
    border: '1px solid #e2e8f0',
    ...slidingToggleGroupNoBorder
  },
  toggleButton: {
    ...slidingToggleButtonNoBorder,
    borderRadius: '14px !important',
    padding: '0 10px',
    fontWeight: 'normal',
    fontSize: 12,
    height: 28,
    minWidth: 80,
    color: '#616161',
    textTransform: 'none',
    zIndex: 2,
    backgroundColor: 'transparent',
    transition: 'color 0.25s ease !important',
    WebkitTapHighlightColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(57, 102, 248, 0.04)',
      border: 'none !important',
      borderColor: 'transparent !important',
      boxShadow: 'none'
    }
  },
  selected: {
    color: '#ffffff !important',
    backgroundColor: 'transparent !important',
    '&:hover': {
      backgroundColor: 'transparent !important'
    }
  },
  slidingIndicator: {
    position: 'absolute',
    top: 2,
    bottom: 2,
    left: 2,
    width: 'calc(50% - 2px)',
    backgroundColor: '#3966f8',
    borderRadius: 14,
    boxShadow: '0 2px 6px rgba(57, 102, 248, 0.2)',
    transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease, visibility 0.2s ease',
    zIndex: 1
  },
  callout: {
    marginTop: 4,
    backgroundColor: '#f8fafc',
    borderLeft: '3px solid #64748b',
    borderRadius: '0 8px 8px 0',
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },
  calloutHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    fontWeight: 700,
    color: '#475569'
  },
  calloutIcon: {
    color: '#64748b',
    flexShrink: 0
  },
  calloutBody: {
    fontSize: 13.5,
    fontWeight: 400,
    color: '#334155',
    lineHeight: 1.5
  }
}));

const DISCDialogComponent = props => {
  const { intl, open, personality, contentTextType, onClose, onExited, onSubmit, style } = props;
  const { classes } = useStyles();

  const [_personality, setPersonality] = useState(personality);
  const [outgoing, setOutgoing] = useState(false);
  const [introverted, setIntroverted] = useState(false);
  const [logical, setLogical] = useState(false);
  const [emotional, setEmotional] = useState(false);
  const shouldUseTwoRows = useMediaQuery('(max-width: 408px)');

  const group1Value = outgoing ? 'outgoing' : introverted ? 'introverted' : '';
  const group1Index = outgoing ? 0 : introverted ? 1 : -1;

  const group2Value = logical ? 'logical' : emotional ? 'emotional' : '';
  const group2Index = logical ? 0 : emotional ? 1 : -1;

  // DISC letter from the *current* axis selection, so the interaction-style result
  // only shows once both axes are chosen (partial/empty selection -> no result).
  const currentDisc =
    outgoing && logical
      ? 'D'
      : outgoing && emotional
      ? 'I'
      : introverted && emotional
      ? 'S'
      : introverted && logical
      ? 'C'
      : '';

  const handleToggleGroup1 = (event, val) => {
    if (val === null) {
      if (outgoing) toggleOutgoing();
      else if (introverted) toggleIntroverted();
    } else {
      if (val === 'outgoing') toggleOutgoing();
      else if (val === 'introverted') toggleIntroverted();
    }
  };

  const handleToggleGroup2 = (event, val) => {
    if (val === null) {
      if (logical) toggleLogical();
      else if (emotional) toggleEmotional();
    } else {
      if (val === 'logical') toggleLogical();
      else if (val === 'emotional') toggleEmotional();
    }
  };

  const _onSubmit = () => {
    if (onSubmit) {
      onSubmit(_personality);
    }
    onClose();
  };

  const toggleOutgoing = () => {
    setOutgoing(!outgoing);
    setIntroverted(false);
  };

  const toggleIntroverted = () => {
    setIntroverted(!introverted);
    setOutgoing(false);
  };

  const toggleLogical = () => {
    setLogical(!logical);
    setEmotional(false);
  };

  const toggleEmotional = () => {
    setEmotional(!emotional);
    setLogical(false);
  };

  const selectPersonality = newPersonality => {
    setPersonality(newPersonality);
    // mergeDialog('DISC', { personality: personality === newPersonality ? '' : newPersonality });
  };

  const selectD = () => selectPersonality('D');

  const selectI = () => selectPersonality('I');

  const selectS = () => selectPersonality('S');
  const selectC = () => selectPersonality('C');

  const selectPersonalityAccordingly = useCallback(() => {
    let newPersonality;
    if (outgoing && logical) {
      newPersonality = 'D';
    } else if (outgoing && emotional) {
      newPersonality = 'I';
    } else if (introverted && emotional) {
      newPersonality = 'S';
    } else if (introverted && logical) {
      newPersonality = 'C';
    }

    if (newPersonality) {
      setPersonality(newPersonality);
    }
  }, [outgoing, introverted, logical, emotional]);

  const selectCharacteristicsAccordingly = useCallback(() => {
    if (_personality) {
      switch (_personality) {
        case 'D':
          setOutgoing(true);
          setIntroverted(false);
          setLogical(true);
          setEmotional(false);
          break;
        case 'I':
          setOutgoing(true);
          setIntroverted(false);
          setLogical(false);
          setEmotional(true);
          break;
        case 'S':
          setOutgoing(false);
          setIntroverted(true);
          setLogical(false);
          setEmotional(true);
          break;
        case 'C':
          setOutgoing(false);
          setIntroverted(true);
          setLogical(true);
          setEmotional(false);
          break;
        default:
          break;
      }
    }
  }, [_personality]);

  useEffect(() => {
    selectPersonalityAccordingly();
  }, [selectPersonalityAccordingly]);

  useEffect(() => {
    setPersonality(personality);
  }, [personality]);

  useEffect(() => {
    selectCharacteristicsAccordingly();
  }, [selectCharacteristicsAccordingly]);

  return (
    <Dialog
      className="disc-dialog"
      style={style}
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth={true}
      slots={{
        transition: SlideTransition
      }}
      slotProps={{
        transition: {
          onExited
        }
      }}
    >
      <DialogTitle>{intl.formatMessage({ id: 'Personality' })}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ flexDirection: 'column' }}>
          <Grid>
            <Typography color="textSecondary">
              {intl.formatMessage({
                id:
                  contentTextType === 'adviser'
                    ? 'adviser_personality_type'
                    : contentTextType === 'candidate'
                    ? 'candidate_personality_type'
                    : 'client_personality_type'
              })}
            </Typography>
          </Grid>
          <Grid style={{ height: shouldUseTwoRows ? 72 : 34 }}>
            <Grid container spacing={1}>
              <Grid size={shouldUseTwoRows ? 12 : 6}>
                <Grid
                  container
                  sx={{
                    justifyContent: 'center'
                  }}
                >
                  <ToggleButtonGroup
                    value={group1Value}
                    exclusive
                    onChange={handleToggleGroup1}
                    classes={{ root: classes.toggleGroup }}
                  >
                    <div
                      className={classes.slidingIndicator}
                      style={{
                        transform: `translateX(${group1Index * 100}%)`,
                        opacity: group1Index === -1 ? 0 : 1,
                        visibility: group1Index === -1 ? 'hidden' : 'visible'
                      }}
                    />
                    <ToggleButton value="outgoing" classes={{ root: classes.toggleButton, selected: classes.selected }}>
                      {intl.formatMessage({ id: 'outgoing' })}
                    </ToggleButton>
                    <ToggleButton
                      value="introverted"
                      classes={{ root: classes.toggleButton, selected: classes.selected }}
                    >
                      {intl.formatMessage({ id: 'introverted' })}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              </Grid>
              <Grid size={shouldUseTwoRows ? 12 : 6}>
                <Grid
                  container
                  sx={{
                    justifyContent: 'center'
                  }}
                >
                  <ToggleButtonGroup
                    value={group2Value}
                    exclusive
                    onChange={handleToggleGroup2}
                    classes={{ root: classes.toggleGroup }}
                  >
                    <div
                      className={classes.slidingIndicator}
                      style={{
                        transform: `translateX(${group2Index * 100}%)`,
                        opacity: group2Index === -1 ? 0 : 1,
                        visibility: group2Index === -1 ? 'hidden' : 'visible'
                      }}
                    />
                    <ToggleButton value="logical" classes={{ root: classes.toggleButton, selected: classes.selected }}>
                      {intl.formatMessage({ id: 'logical' })}
                    </ToggleButton>
                    <ToggleButton
                      value="emotional"
                      classes={{ root: classes.toggleButton, selected: classes.selected }}
                    >
                      {intl.formatMessage({ id: 'emotional' })}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid>
            <Grid container spacing={1} sx={{ flexDirection: 'column' }}>
              {[
                {
                  value: 'D',
                  color: '#EA8462',
                  label: intl.formatMessage({ id: 'personality_D' }),
                  description: `${discTag('D', intl.locale)} · ${discHint('D', intl.locale)}`,
                  onClick: selectD
                },
                {
                  value: 'I',
                  color: '#EAC862',
                  label: intl.formatMessage({ id: 'personality_I' }),
                  description: `${discTag('I', intl.locale)} · ${discHint('I', intl.locale)}`,
                  onClick: selectI
                },
                {
                  value: 'S',
                  color: '#62EA84',
                  label: intl.formatMessage({ id: 'personality_S' }),
                  description: `${discTag('S', intl.locale)} · ${discHint('S', intl.locale)}`,
                  onClick: selectS
                },
                {
                  value: 'C',
                  color: '#62A6EA',
                  label: intl.formatMessage({ id: 'personality_C' }),
                  description: `${discTag('C', intl.locale)} · ${discHint('C', intl.locale)}`,
                  onClick: selectC
                }
              ].map(({ value, color, label, description, onClick }) => (
                <Grid key={label}>
                  <Grid
                    container
                    spacing={1}
                    sx={{
                      alignItems: 'center'
                    }}
                  >
                    <Grid>
                      <span>
                        <Button
                          className="portfolio-default-button button-line-height dialogue__personality"
                          style={{
                            background: _personality === value ? color : '#cccccc',
                            color: _personality === value ? '#ffffff' : '#000'
                          }}
                          variant="contained"
                          onClick={onClick}
                        >
                          {label}
                        </Button>
                      </span>
                    </Grid>
                    <Grid>
                      <Typography>{description}</Typography>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
          {currentDisc && (
            <Grid>
              <div
                className={classes.callout}
                style={
                  DISC_CALLOUT_COLORS[currentDisc]
                    ? {
                        backgroundColor: DISC_CALLOUT_COLORS[currentDisc].bg,
                        borderLeftColor: DISC_CALLOUT_COLORS[currentDisc].accent
                      }
                    : undefined
                }
              >
                <div
                  className={classes.calloutHeader}
                  style={
                    DISC_CALLOUT_COLORS[currentDisc] ? { color: DISC_CALLOUT_COLORS[currentDisc].accent } : undefined
                  }
                >
                  <MessageSquare
                    className={classes.calloutIcon}
                    size={16}
                    color={DISC_CALLOUT_COLORS[currentDisc] ? DISC_CALLOUT_COLORS[currentDisc].accent : undefined}
                  />
                  <span>{intl.formatMessage({ id: 'Approach' })}:</span>
                </div>
                <div className={classes.calloutBody}>{intl.formatMessage({ id: `${currentDisc}_approach` })}</div>
              </div>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {intl.formatMessage({ id: 'Close' })}
        </Button>
        <Button onClick={_onSubmit} color="primary">
          {intl.formatMessage({ id: 'OK' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default injectIntl(DISCDialogComponent);
